<template>
  <div data-view="billing-account-add-new-form">
    <form @:submit.prevent="onSubmit">
      <section class="add-billing-details">
        <h5 class="mb-2">
          {{ $t('add_billing_details') }}
        </h5>

        <v-text-field
          v-model="companyName"
          :label="$t('company_name')"
          :error-messages="companyNameErrors"
          :persistent-hint="true"
          hint=" "
        >
          <template v-slot:message>
            <div class="billing-account-add-new-form--input-hint">
              {{ $t('billing_account_hint_1') }}
              <a
                :href="viesLink"
                target="_blank"
              >{{ $t('billing_account_hint_2') }}</a>
              {{ $t('billing_account_hint_3') }}
            </div>
          </template>
        </v-text-field>

        <magic-container>
          <v-autocomplete
            v-model="country"
            :items="countries"
            :label="$t('billing_country')"
            item-title="name"
            item-value="code"
            class="country-dropdown"
            :error-messages="countryErrors"
            return-object
            @update:model-value="onChangeCountry"
          />

          <v-text-field
            v-model="companyNumber"
            :label="$t('company_number')"
            :error-messages="companyNumberErrors"
          />
        </magic-container>

        <VatForm
          v-model="vat"
          ref="vatForm"
          :country="country"
          :on-change-not-vat-registered="v => notVatRegistered = v"
          :on-change-loading="v => isCheckingVat = v"
        />

        <v-text-field
          v-model="companyAddress"
          :label="$t('company_address')"
          :error-messages="companyAddressErrors"
        />

        <v-text-field
          v-model="companyAddressOpt"
          :label="$t('company_address_opt')"
        />

        <magic-container>
          <v-text-field
            v-model="postalCode"
            :label="$t('postal_code')"
            :error-messages="postalCodeErrors"
          />

          <v-text-field
            v-model="city"
            :label="$t('city')"
            :error-messages="cityErrors"
          />
        </magic-container>
      </section>

      <LinkToForm
        method="invoice"
        :billing-account="item"
        :link-countries="linkCountries"
      />

      <section class="invoice-preferences">
        <h5>
          {{ $t('add_invoice_preferences') }} <span>{{ $t('optional') }}</span>
        </h5>

        <v-row>
          <v-col cols="6">
            <v-autocomplete
              v-model="invoiceLanguage"
              :items="invoiceLanguages"
              :label="$t('invoice_document_language')"
              item-title="name"
              item-value="code"
              return-object
            />
          </v-col>
        </v-row>

        <v-row>
          <v-text-field
            ref="emailField"
            v-model="billingEmail"
            :label="$t('company_billing_email')"
            hint=" "
            :rules="emailRules"
            persistent-hint
            @keyup.enter="addEmail"
            @blur="blurEmail"
          >
            <template #message="{ message }">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="message"></span>
              <v-btn
                class="add-email-btn elevation-0"
                color="primary"
                :disabled="!isBilligEmailValid"
                variant="tonal"
                @click="addEmail"
              >
                {{ $t('add_email') }}
              </v-btn>
            </template>
          </v-text-field>
        </v-row>

        <v-row>
          <Removables
            v-if="billingEmails.length > 0"
            :items="billingEmails"
            :is-disabled="isLoading"
            removable-class="d-flex align-center justify-between"
            :on-remove="removeEmail"
          >
            <template v-slot:item="scope" >
              <span class="align-self-center">{{ scope.item }}</span>
            </template>
          </Removables>
        </v-row>
      </section>

      <v-row>
        <div
          v-if="v$.$error"
          xs12
          class="form-error-field"
        >
          <v-icon color="white" size="13px"> warning </v-icon>
          <span>
            {{ $t("please_fill_out_the_missing_fields") }}
          </span>
        </div>
      </v-row>

      <v-row>
        <div>
          <div class="billing-account-actions">
            <v-btn
              :disabled="isLoading || isCheckingVat"
              variant="text"
              @click="onCancelClick"
            >
              {{ $t('cancel') }}
            </v-btn>

            <v-btn
              color="primary"
              :loading="isLoading || isCheckingVat"
              :class="submitClass"
              elevation="0"
              @click="onSubmit"
            >
              {{ acceptBtnText }}
            </v-btn>
          </div>
        </div>
      </v-row>
    </form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators'
  import { isNum, isStr, isNonEmptyStr } from '@/utils';
  import LinkToForm from './LinkTo';
  import Removables from '../../lists/Removables';
  import VatForm from './Vat';

  export default {
    components: {
      LinkToForm,
      Removables,
      VatForm
    },
    setup () {
      return {
        v$: useVuelidate()
      }
    },
    validations() {
      return {
        companyName: {
          required,
        },
        companyAddress: {
          required,
        },
        postalCode: {
          required,
        },
        city: {
          required,
        },
        country: {
          required,
        },
        companyNumber: {
          required,
        },
      };
    },
    props: {
      item: {
        type: Object,
        required: true,
      },
      isOn: {
        type: Boolean,
        required: true,
      },
      isLoading: {
        type: Boolean,
        required: true,
      },
      onCancel: {
        type: Function,
        required: true,
      },
      onConfirm: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        viesLink: import.meta.env.VUE_APP_HELP_VIES_URL,
        id: '',
        companyName: '',
        companyAddress: '',
        companyAddressOpt: '',
        postalCode: '',
        city: '',
        country: null,
        companyNumber: '',
        vat: '',
        notVatRegistered: false,
        isCheckingVat: false,
        linkedCountries: [],
        billingEmail: '',
        billingEmails: [],
        invoiceLanguage: {
          name: 'English',
          code: 'en',
        },
        languages: {
          EN: [
            {
              name: 'English',
              code: 'en',
            },
          ],
          DE: [
            {
              name: 'English',
              code: 'en',
            },
            {
              name: 'German',
              code: 'de',
            },
          ],
          FR: [
            {
              name: 'English',
              code: 'en',
            },
            {
              name: 'French',
              code: 'fr',
            },
          ],
        },
        emailRules: [
          () => this.isBilligEmailValid || this.$t('invalid_email')
        ],
        submitClass: 'submit-btn'
      };
    },
    computed: {
      acceptBtnText() {
        if (this.isLoading || this.isCheckingVat) {
          return this.$t('loading');
        }
        if (isNum(this.item.id)) {
          return this.$t('update_billing_account');
        }
        return this.$t('add_billing_account');
      },
      countries() {
        return this.$store.getters['config/countries'];
      },
      invoiceLanguages() {
        const { country, languages } = this;
        const defaultLanguage = languages.EN;

        if (!country || !isStr(country.code)) {
          return defaultLanguage;
        }

        return languages[country.code] || defaultLanguage;
      },
      companyNameErrors() {
        return this.v$.companyName.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('please_enter_legal_entity');
          }
          return error.$message;
        });
      },
      companyAddressErrors() {
        return this.v$.companyAddress.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('please_enter_the_company_address');
          }
          return error.$message;
        });
      },
      postalCodeErrors() {
        return this.v$.postalCode.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('please_enter_the_zip_postal_code');
          }
          return error.$message;
        });
      },
      cityErrors() {
        return this.v$.city.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('please_enter_the_city');
          }
          return error.$message;
        });
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('please_select_the_country');
          }
          return error.$message;
        });
      },
      companyNumberErrors() {
        return this.v$.companyNumber.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('please_enter_your_company_number');
          }
          return error.$message;
        });
      },
      isBilligEmailValid() {
        const regexEmail = /\S+@\S+\.\S+/;

        return regexEmail.test(this.billingEmail);
      }
    },
    watch: {
      isOn(newValue) {
        this.submitClass = isNum(this.item?.id) ? 'update-btn' : 'submit-btn';

        if (newValue) {
          return this.extractBillingAddress(this.item);
        }

        this.clear();
        this.v$.$reset();
      },
      billingEmail() {
        this.blurEmail();
      }
    },
    created() {
      this.getCountries();
    },
    methods: {
      getCountries() {
        return this.$store.dispatch('config/getCountries');
      },
      extractBillingAddress(item) {
        if (!isNum(item.id)) {
          return;
        }

        const {
          id,
          legal_business_name,
          address,
          address_opt,
          zip,
          city,
          country,
          company_number,
          vat_id,
          linked_countries,
          language,
          is_vat_registered,
          invoice_emails,
        } = item;

        this.id = id;
        this.companyName = legal_business_name || '';
        this.companyAddress = address || '';
        this.companyAddressOpt = address_opt || '';
        this.postalCode = zip || '';
        this.city = city || '';
        this.country = country || {};
        this.companyNumber = company_number || '';
        this.vat = vat_id || '';
        this.$refs.vatForm.originalVat = vat_id;
        this.linkedCountries = linked_countries || [];
        this.invoiceLanguage = language;
        this.billingEmails = invoice_emails.map((e) => e.email) || [];
        this.notVatRegistered = is_vat_registered === 0;
        this.$refs.vatForm.notVatRegistered = is_vat_registered === 0;
      },
      linkCountries(items) {
        this.linkedCountries = items;
      },
      onChangeCountry() {
        this.invoiceLanguage = {
          name: 'English',
          code: 'en',
        };
      },
      canSubmitRequest() {
        const isValidVat = this.$refs.vatForm.isValid;

        return this.companyName !== ''
          && this.companyAddress !== ''
          && this.postalCode !== ''
          && this.city !== ''
          && isStr(this.country?.code)
          && this.companyNumber !== ''
          && isStr(this.invoiceLanguage.code)
          && !this.hasFormErrors
          && isValidVat;
      },
      blurEmail() {
        if (!isNonEmptyStr(this.billingEmail)) {
          this.$refs.emailField.reset();
        }
      },
      onSubmit() {
        this.v$.$touch();
        this.$refs.vatForm.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        const {
          id,
          companyName,
          companyAddress,
          companyAddressOpt,
          postalCode,
          city,
          country,
          companyNumber,
          vat,
          linkedCountries,
          notVatRegistered,
          invoiceLanguage,
          billingEmails,
        } = this;

        let isVatRegistered = notVatRegistered ? 0 : 1;
        // when creating billing if country doesn't need vat
        // and the field is empty -> send isVatRegistered: 0; to pass the BE validation
        if (
          this.vat === '' &&
          isVatRegistered &&
          !this.$refs.vatForm.countryNeedsVat &&
          !this.isEdit
        ) {
          isVatRegistered = 0;
        }

        const query = {
          legal_business_name: companyName,
          address: companyAddress,
          address_opt: companyAddressOpt,
          zip: postalCode,
          city,
          country: country.code,
          company_number: companyNumber,
          vat_id: vat,
          linked_countries: linkedCountries.map((c) => c.code),
          language: invoiceLanguage.code,
          is_vat_registered: isVatRegistered,
          invoice_emails: billingEmails,
        };

        if (id) {
          query.id = id;
        }

        this.onConfirm(query);
      },
      onCancelClick() {
        this.billingEmail = '';
        this.blurEmail();
        this.v$.$reset();
        this.onCancel();
      },
      clear() {
        this.id = '';
        this.companyName = '';
        this.companyAddress = '';
        this.companyAddressOpt = '';
        this.postalCode = '';
        this.city = '';
        this.country = null;
        this.companyNumber = '';
        this.linkedCountries = [];
        this.billingEmail = '';
        this.billingEmails = [];
        this.invoiceLanguage = {
          name: 'English',
          code: 'en',
        };
        this.notVatRegistered = false;
        this.isCheckingVat = false;
        this.vat = '';
        this.$refs.vatForm.originalVat = 'XXXX';
        this.$refs.vatForm.v$.$reset();
      },
      addEmail() {
        const { isBilligEmailValid, billingEmail, billingEmails } = this;

        if (billingEmail
          && !billingEmails.includes(billingEmail)
          && isBilligEmailValid
        ) {
          billingEmails.push(billingEmail);
          this.$refs.emailField.reset();
          this.billingEmail = '';
        }
      },
      removeEmail(item) {
        this.billingEmails = this.billingEmails.filter(email => email !== item);
      }
    }
  };
</script>

<style lang="scss">
[data-view='billing-account-add-new-form'] {
  min-height: auto;

  h5 {
    font-size: 16px;
    color: #424242;

    .v-theme--dark & {
      color: rgb(var(--v-theme-on-surface));
    }
  }

  .add-billing-details {
    margin-top: 24px;
    padding: 0 16px;
  }

    .billing-account-add-new-form--input-hint {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;

      a,
      a:visited {
        color: #1E88E5;
        font-weight: 700;
        font-size: 12px;
        line-height: 16px;
        text-decoration: none;
      }
    }

    .invoice-preferences {
      margin-top: 24px;
      padding: 16px;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
      border-radius: 4px;

    h5 {
      margin-bottom: 8px;

      span {
        font-size: 14px;
        font-weight: 400;
        color: #9e9e9e;
      }
    }

    .v-list-item {
      min-height: 0;
    }

      .add-email-btn {
        margin: 0 0 0 auto;
        display: flex;
      }
    }

    [data-component-group="lists"] {
      &[data-component="removables"] {
        .v-list {
          padding-top: 0;
        }

        .v-list-item__content {
          width: 100%;
        }
      }
    }

  [data-component-group='lists'] {
    &[data-component='removables'] {
      .v-list {
        padding-top: 0;
      }
    }
  }

  .form-error-field {
    display: flex;
    margin: 40px 8px 0 8px;
    color: white;
    font-size: 14px;
    font-weight: 400;
    background: rgb(var(--v-theme-error));
    border-radius: 2px;

    .v-icon {
      margin-right: 4px;
    }
  }

  .billing-account-actions {
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;

    .submit-btn,
    .update-btn {
      margin-left: 16px;
      width: 158px;

      .spinner {
        margin-right: 4px;
      }

      &:disabled {
        color: white !important;
      }
    }

    .update-btn {
      width: 180px;
    }
  }
}
</style>
